import { parseISO, getDayOfYear, addDays, isLeapYear } from "date-fns";

import { updateOverlayFromR2, COLORS, fetchFromAcis, calcSeasonYear, splitIntoYears } from "../../../utilities";

export const TAB_NAME = 'Spring Freeze Damage';

const calculatePrediction = (dailyGDH, doyOfSeasonStart, alpha, beta, gamma, delta, matDegC) => {
  const THRESHOLD = 1000;
  let t = 0;

  for (let i = 0; i < dailyGDH.length; i++) {
    const accumulatedGDHs = dailyGDH[i][1];

    // t is number of days since start_date (with start_date being t = 1)
    t += 1;
    
    // Where the magic happens
    const C_hat = (alpha * t) + (beta * accumulatedGDHs);

    if (C_hat >= THRESHOLD) {
      // Bias correction based on 30 year mean annual temp. at location
      const bias = gamma + delta * matDegC;
      t = t - Math.round(bias);
  
      // Day that we started checking plus days since then
      let doy = doyOfSeasonStart + t;
      
      // Adjust doy if over or under a full year
      if (doy > 365) {
        doy -= 365
      } else if (doy < 1) {
        doy += 365
      }
      return doy
    }
  }

  return null;
}

const fToc = (degreesFahr) => {
  return (degreesFahr - 32) * (5 / 9);
};

const calculateGDH = (dailyHourlyTemps, baseTemp, inC=false) => {
  if (inC) {
    const baseTempC = fToc(baseTemp);
    return dailyHourlyTemps.reduce((sum, tempF) => {
      const tempC = fToc(tempF);
      if (baseTempC < tempC) {
        sum += (tempC - baseTempC);
      }
      return sum;
    }, 0);
  } else {
    return dailyHourlyTemps.reduce((sum, tempF) => {
      if (baseTemp < tempF) {
        sum += (tempF - baseTemp);
      }
      return sum;
    }, 0);
  }
}

const calcDaytimeTemp = (Tmax, Tmin, L, h) => {
  return (Tmax - Tmin) * Math.sin((Math.PI / (L + 4)) * h) + Tmin;
};

const calcNighttimeTemp = (Ds, Tmin, L, h) => {
  return Ds - ((Ds - Tmin) / (Math.log(24-L)) * Math.log(h-L))
};

const calcSinusoidalTemps = (Tmin, Tmax, L) => {
  const sinusoidalResults = [];
  
  // Calculate the last hour of daytime
  const lPrime = Math.round(L);

  for (let h = 1; h <= 24; h++) {
    let temp;
    if (h <= lPrime) {
      // daytime function
      temp = calcDaytimeTemp(Tmax, Tmin, L, h - 1);
    } else {
      // nighttime function
      const Ds = calcDaytimeTemp(Tmax, Tmin, L, L);   // Calculate temperature at sunset
      temp = calcNighttimeTemp(Ds, Tmin, L, h);
    }

    sinusoidalResults.push(temp)
  }

  return sinusoidalResults;
};

const calcSunRevolutionAngleRadians = (dayOfYear) => {
  return 0.2163108 + 2 * Math.atan(0.9671396 * Math.tan(0.0086 * (dayOfYear - 186)));
};

const calcSunDeclinationAngleRadians = (sunRevAngRads) => {
  return Math.asin(0.39795 * Math.cos(sunRevAngRads));
};

const calcDaylength = (doy, lat) => {
  const P = 0.8333;
  const sunRevAngRads = calcSunRevolutionAngleRadians(doy);
  const sunDecAngRads = calcSunDeclinationAngleRadians(sunRevAngRads);

  const pp180 = (P * Math.PI) / 180;
  const lp180 = (lat * Math.PI) / 180;
  return 24 - (24 / Math.PI) * Math.acos((Math.sin(pp180) + Math.sin(lp180) * Math.sin(sunDecAngRads)) / (Math.cos(lp180) * Math.cos(sunDecAngRads)));
};

const interpolateDailyToHourlyTemperatures = (targetDate, Tmin, Tmax, lat) => {
  // Get day of year of the target date
  const doy = getDayOfYear(parseISO(targetDate));

  // Calculate daylength of target date
  const L = calcDaylength(doy, lat);

  // Interpolate and return results
  return calcSinusoidalTemps(Tmin, Tmax, L);
};

const OAK_PHENOLOGY_CONFIGURATIONS = {
  'white_oak': [{
    name: 'leaf',
    alpha: 6.410,
    beta: 0.024,
    gamma: -21.965,
    delta: 2.099,
    baseTemp: 46.985
  },{
    name: 'bloom',
    alpha: 5.078,
    beta: 0.014,
    gamma: null,
    delta: null,
    baseTemp: 32.6606
  }],
  'red_oak': [{
    name: 'leaf',
    alpha: 6.390,
    beta: 0.008,
    gamma: -13.271,
    delta: 1.456,
    baseTemp: 32.7146
  },{
    name: 'bloom',
    alpha: 5.949,
    beta: 0.007,
    gamma: -12.011,
    delta: 1.368,
    baseTemp: 36.9356
  }]
};

const calculatePhenologyDates = (acisData, mat, lat, oakSpecies) => {
  const doySeasonStart = -31
  const phenologyInfo = OAK_PHENOLOGY_CONFIGURATIONS[oakSpecies];

  // Interpolate from daily mint and maxt to hourly temperature using sine/log method
  const hourlyTemps = acisData.map(([date, mint, maxt]) => {
    return [date, interpolateDailyToHourlyTemperatures(date, mint, maxt, lat)];
  });

  const phenologyResults = {};
  phenologyInfo.forEach(({ name, alpha, beta, gamma, delta, baseTemp }) => {
    // Calculate daily Growing Degree Hour accumulations, use Celsius version of GDH calculation function
    let accumulatedGDH = 0
    const dailyGDH = []            // Becomes a list of [date string 'YYYY-MM-DD', GDH accumulation]
    for (let i = 0; i < hourlyTemps.length; i++) {
      const [dayDate, temps] = hourlyTemps[i];
      const dayGDHs = calculateGDH(temps, baseTemp, true);
      accumulatedGDH += dayGDHs;
      dailyGDH.push([dayDate, accumulatedGDH]);
    }

    // Calculate predicted day of year that phenology will occur
    const doy = calculatePrediction(dailyGDH, doySeasonStart, alpha, beta, gamma, delta, fToc(mat));
    phenologyResults[name] = doy;
  });

  return phenologyResults;
};

const doyToChartTimestamp = (doy, isLeap) => {
  if (doy === null) {
    return null;
  }
  
  if (!isLeap && 59 < doy) {
    doy = doy + 1
  }
  const timestampYear = 2024;
  const dateObj = addDays(new Date(timestampYear, 0, 1), doy - 1);
  return dateObj.getTime();
};

const isoDateStringToTimestamp = (dateStr, isLeap) => {
  if (dateStr === null) return null;
  const doy = getDayOfYear(parseISO(dateStr));
  return doyToChartTimestamp(doy, isLeap);
};

export const calcOakFreezeChartData = (temps, mat, lat, threshold, oakSpecies) => {
  const newChartData = {
    tempThreshold: threshold,
    years: [],
    mints: [],
    leafTimestamps: [],
    bloomTimestamps: [],
    numDamagingFreezeEvents: [],
    finalFreezeEventTimestamps: []
  };

  const yearlyData = splitIntoYears(temps, '12-01', '06-30');
  yearlyData.forEach(yearOfData => {
    // Calculate leaf and bloom phenology day of year
    const { leaf, bloom } = calculatePhenologyDates(yearOfData, mat, lat, oakSpecies);
    
    // Add timestamps and year to results
    const seasonYear = parseInt(yearOfData[0][0].slice(0,4)) + 1;
    const isLeap = isLeapYear(new Date(seasonYear,0,1));
    newChartData.years.push(seasonYear);
    const leafTimestamp = doyToChartTimestamp(leaf, isLeap);
    newChartData.leafTimestamps.push(leafTimestamp);
    newChartData.bloomTimestamps.push(doyToChartTimestamp(bloom, isLeap));

    // Filter to dates crossing threshold
    const seasonEndTimestamp = isoDateStringToTimestamp(`${seasonYear}-07-01`, isLeap);
    const yearMints = [];
    const freezeDays = yearOfData.reduce((res, day) => {
      yearMints.push([day[0].slice(5), day[1]]);
      const dayTimestamp = isoDateStringToTimestamp(day[0], isLeap);
      
      if (dayTimestamp < seasonEndTimestamp && leafTimestamp < dayTimestamp && day[1] <= threshold) {
        res.push(dayTimestamp);
      }
      return res;
    }, []);
    newChartData.mints.push(yearMints);

    // Add last one to results
    const finalFreezeDay = yearOfData.length > 89 ? yearOfData.toReversed().find(d => d[1] < threshold) : [null];
    if (finalFreezeDay) {
      newChartData.finalFreezeEventTimestamps.push(isoDateStringToTimestamp(finalFreezeDay[0], isLeap));
    } else {
      newChartData.finalFreezeEventTimestamps.push(null);
    }

    // Add number of freeze events after breaking leaf buds in the season
    newChartData.numDamagingFreezeEvents.push(bloom === null ? 0 : freezeDays.length);
  });

  return newChartData;
};

export function updateChartData({ tabsSharedState, selectedTab, selectedSubTab, selectedLocation, rawChartData, pastLocations }, _, { setRawChartData, setChartData }) {
  if (
    selectedTab === TAB_NAME &&
    selectedLocation in rawChartData && 'mintMaxt' in rawChartData[selectedLocation]
  ) {
    let newChartData;
    if (selectedSubTab.split(' ')[1] === 'Oak') {
      const specificDataName = 'mat';
      const locInfo = pastLocations[selectedLocation];
      if (!(specificDataName in rawChartData[selectedLocation])) {
        const coords = `${locInfo.lng},${locInfo.lat}`;

        // Get the mean average annual temperature from 1991 to 2020 for the location
        const elems = {
          loc: coords,
          grid: 'nrcc-nn',
          date: '2020-12-31',
          elems:[{
            name: 'avgt',
            interval: [30],
            duration: 30,
            reduce: 'mean'
          }]
        };

        return fetchFromAcis(elems)
          .then(rawData => {
            const newRawChartData = { ...rawChartData };
            newRawChartData[selectedLocation][specificDataName] = rawData[0][1];
            return () => setRawChartData(newRawChartData);
          });
      } else {
        let threshold = tabsSharedState.oakFreezeDamageTemp.value;
        if (typeof threshold === 'function') {
          threshold = threshold();
        }
        if (threshold.length === 1) {
          newChartData = calcOakFreezeChartData(rawChartData[selectedLocation]['mintMaxt'], rawChartData[selectedLocation][specificDataName], locInfo.lat, threshold,selectedSubTab.split(' ').slice(0,2).join('_').toLowerCase());
        }
      }
    }

    return () => setChartData(newChartData);
  }
  return () => {};
}

export function updateOverlay({ biomassInfo, selectedSubTab, tabsSharedState }, _, { updateOverlayData }) {
  let isReversed = false;
  let binColors = undefined;
  let thresholdFn = undefined;
  let extraLegendInfo = { shiftLabels: -5 };
  let jsonFileDataKey = 'departures';
  const reformattedName = selectedSubTab.toLowerCase().split(' ').join('_');
  let jsonFileName = `${reformattedName}.json`;
  let countiesWithSpecies = [];

  if (selectedSubTab.split(' ')[1] === 'Oak') {
    jsonFileDataKey = 'counts';
    if (tabsSharedState['ofdOverlaySelector'].value === 'Current Year') {
      thresholdFn = (i) => i === 0 ? 1 : Infinity;
      binColors = [COLORS.green, COLORS.darkRed];
      isReversed = true;
      extraLegendInfo = {
        isPercentage: false,
        comp: 'lt',
        titleFn: () => 'Likelihood of Damage',
        labelsFn: () => ['High', 'Low'],
        shiftLabels: -40
      };
    } else {
      thresholdFn = (i) => i * 10;
      binColors = [COLORS.darkRed, COLORS.orange, COLORS.yellow, COLORS.green];
      extraLegendInfo = {
        comp: 'le',
        titleFn: (c) => 'Years with Damaging Events',
        isPercentage: false,
        labelsFn: () => ['>20', '11-20', '1-10', '0'],
        shiftLabels: -36
      };
    }
  }

  if (countiesWithSpecies.length === 0) {
    let biomassThreshold = tabsSharedState['biomassThresholdSelector'].value;
    if (typeof biomassThreshold === 'function') biomassThreshold = biomassThreshold(selectedSubTab);
    const maxBiomassByCounty = Object.entries(biomassInfo).find(([name,obj]) => jsonFileName.includes(name))[1];
    Object.keys(maxBiomassByCounty).forEach(countyFips => {
      if (biomassThreshold <= maxBiomassByCounty[countyFips]) {
        countiesWithSpecies.push(countyFips);
      }
    });
  }

  return updateOverlayFromR2(jsonFileName, jsonFileDataKey, binColors, isReversed, thresholdFn, extraLegendInfo, countiesWithSpecies)
    .then(d => () => updateOverlayData(d));
}

export function updateTitle({selectedSubTab, display, overlayDataDate, chartData, tabsSharedState}) {
  try {
    let sdate, edate;
    if (selectedSubTab.includes('Oak')) {
      if (display === 'map') {
        const seasonYear = calcSeasonYear(overlayDataDate, '2020-12-01', '2021-06-30');
        if (tabsSharedState.ofdOverlaySelector.value === 'Current Year') {
          sdate = seasonYear;
        } else {
          sdate = seasonYear - 30;
          edate = seasonYear - 1;
        }
      } else {
        sdate = chartData.years[0];
        edate = chartData.years[chartData.years.length - 1];
      }
    }

    const type = selectedSubTab;
    return {
      type,
      sdate,
      edate
    }
  } catch {
    return null;
  }
}